import { useState } from 'react';
import { useSIEMConfigs, saveSIEMConfig, testSIEMConnection, toggleSIEMSource } from '../../hooks/useSecOps';

interface LogSource {
  id: string;
  name: string;
  type: string; // syslog, api, agent, cloud, file
  endpoint?: string;
  enabled: boolean;
  status: string; // connected, disconnected, error, pending
  events_per_day?: number;
  last_event_at?: string;
  created_at: string;
}

const STATUS_INDICATORS: Record<string, string> = {
  connected: 'bg-green-500',
  disconnected: 'bg-gray-400',
  error: 'bg-red-500',
  pending: 'bg-yellow-500',
};

const SOURCE_TYPES = ['syslog', 'api', 'agent', 'cloud', 'file'];

export default function LogSourcesPage() {
  const { data, loading, error, refresh } = useSIEMConfigs();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'api', endpoint: '', auth_token: '' });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; success: boolean; message: string } | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);

  const configs = (data?.configs ?? []) as LogSource[];

  async function handleSave() {
    if (!form.name) return;
    setSaving(true);
    try {
      await saveSIEMConfig(form);
      setShowAdd(false);
      setForm({ name: '', type: 'api', endpoint: '', auth_token: '' });
      refresh();
    } catch { /* handled */ } finally {
      setSaving(false);
    }
  }

  async function handleTest(id: string) {
    setTesting(id);
    setTestResult(null);
    try {
      const result = await testSIEMConnection(id);
      setTestResult({ id, ...result });
    } catch (err) {
      setTestResult({ id, success: false, message: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTesting(null);
    }
  }

  async function handleToggle(id: string, enabled: boolean) {
    setToggling(id);
    try {
      await toggleSIEMSource(id, enabled);
      refresh();
    } catch { /* handled */ } finally {
      setToggling(null);
    }
  }

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-96 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Log Sources</h1>
        <button onClick={() => setShowAdd(true)} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
          Add Source
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm flex items-center justify-between">{error} <button onClick={refresh} className="text-sm font-medium underline">Retry</button></div>}

      {configs.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-gray-500 mb-4">No log sources configured. Add your first log source to start collecting security events.</p>
          <button onClick={() => setShowAdd(true)} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
            Add Log Source
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {configs.map(source => (
            <div key={source.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${STATUS_INDICATORS[source.status] ?? 'bg-gray-400'}`} title={source.status} />
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">{source.name}</h3>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                      <span className="capitalize">{source.type}</span>
                      {source.endpoint && <span className="font-mono text-xs">{source.endpoint}</span>}
                      <span className="capitalize">{source.status}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleToggle(source.id, !source.enabled)}
                    disabled={toggling === source.id}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${source.enabled ? 'bg-indigo-600' : 'bg-gray-300'} ${toggling === source.id ? 'opacity-50' : ''}`}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${source.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <button onClick={() => handleTest(source.id)} disabled={testing === source.id}
                    className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-600 hover:border-indigo-400 hover:text-indigo-600 disabled:opacity-40">
                    {testing === source.id ? 'Testing...' : 'Test'}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-6 mt-3 text-xs text-gray-500">
                {source.events_per_day != null && <span>{source.events_per_day.toLocaleString()} events/day</span>}
                {source.last_event_at && <span>Last event: {new Date(source.last_event_at).toLocaleString()}</span>}
                <span>Added: {new Date(source.created_at).toLocaleDateString()}</span>
              </div>
              {/* Test result */}
              {testResult && testResult.id === source.id && (
                <div className={`mt-3 p-3 rounded text-sm ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {testResult.success ? 'Connection successful' : `Connection failed: ${testResult.message}`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Source Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Log Source</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Source name..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                  {SOURCE_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint / URL</label>
                <input type="text" value={form.endpoint} onChange={e => setForm(f => ({ ...f, endpoint: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="https://... or syslog://..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Auth Token (if required)</label>
                <input type="password" value={form.auth_token} onChange={e => setForm(f => ({ ...f, auth_token: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="API key or token..." />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowAdd(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.name}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Add Source'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
